import React, { useEffect, useState } from 'react';

function Stars({ rating }) {
  const value = Math.max(0, Math.min(5, Number(rating) || 0));
  return (
    <span className="reviews-stars" aria-label={`${value} out of 5 stars`}>
      {'★★★★★'.split('').map((star, index) => (
        <span key={index} className={index < value ? 'is-filled' : ''} aria-hidden="true">{star}</span>
      ))}
    </span>
  );
}

function ReviewImages({ images = [] }) {
  if (!images.length) return null;
  return (
    <div className="reviews-card__images" aria-label="Customer review images">
      {images.map((image) => (
        <a key={image.id || image.url} href={image.url} target="_blank" rel="noopener noreferrer">
          <img src={image.url} alt={image.filename || 'Customer shared review'} loading="lazy" />
        </a>
      ))}
    </div>
  );
}

function ReviewCard({ review, featured = false }) {
  return (
    <article className="reviews-card">
      <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
        <Stars rating={review.rating} />
        {featured ? (
          <span className="reviews-card__badge">Featured story</span>
        ) : (
          <span className="reviews-card__badge reviews-card__badge--verified">✓ Verified purchase</span>
        )}
      </div>
      <blockquote>“{review.comment}”</blockquote>
      <div className="reviews-card__meta">
        <strong>{review.reviewerName || review.name || 'Hunar customer'}</strong>
        {review.productNames && <span>{review.productNames}</span>}
      </div>
      <ReviewImages images={review.images} />
    </article>
  );
}

export default function ReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [featuredReviews, setFeaturedReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    Promise.all([
      fetch('/api/reviews').then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.msg || 'Reviews are unavailable right now.');
        return Array.isArray(data.reviews) ? data.reviews : [];
      }),
      fetch('/api/cms/homepage').then((response) => response.ok ? response.json() : []).catch(() => []),
    ]).then(([publicReviews, sections]) => {
      if (!active) return;
      setReviews(publicReviews);
      const testimonialSection = (Array.isArray(sections) ? sections : [])
        .find((section) => section.section_type === 'testimonials');
      const stories = Array.isArray(testimonialSection?.content_json?.reviews)
        ? testimonialSection.content_json.reviews
        : [];
      setFeaturedReviews(stories.filter((review) => review?.comment));
    }).catch((loadError) => {
      if (active) setError(loadError.message || 'Reviews are unavailable right now.');
    }).finally(() => {
      if (active) setLoading(false);
    });

    return () => { active = false; };
  }, []);

  return (
    <main className="container reviews-page py-5" aria-labelledby="reviews-title">
      <div className="reviews-page__header">
        <p className="reviews-page__eyebrow">Customer love</p>
        <h1 id="reviews-title" className="brand-font">Reviews from the Hunar community</h1>
        <p>See what customers say about their handmade pieces, including photos shared after delivery.</p>
      </div>

      {error && <div className="alert alert-warning" role="status">{error}</div>}
      {loading && <div className="reviews-page__loading" role="status">Loading reviews…</div>}

      {!loading && featuredReviews.length > 0 && (
        <section className="reviews-page__section" aria-labelledby="featured-reviews-title">
          <div className="reviews-page__section-heading">
            <h2 id="featured-reviews-title">Featured customer stories</h2>
            <span>From the Hunar team</span>
          </div>
          <div className="reviews-grid">
            {featuredReviews.map((review, index) => <ReviewCard key={`${review.name || 'story'}-${index}`} review={review} featured />)}
          </div>
        </section>
      )}

      {!loading && (
        <section className="reviews-page__section" aria-labelledby="verified-reviews-title">
          <div className="reviews-page__section-heading">
            <h2 id="verified-reviews-title">Verified reviews</h2>
            <span>{reviews.length} published</span>
          </div>
          {reviews.length > 0 ? (
            <div className="reviews-grid">
              {reviews.map((review) => <ReviewCard key={review.id} review={review} />)}
            </div>
          ) : (
            <div className="reviews-page__empty">No verified reviews have been published yet. Be the first to share your experience after delivery.</div>
          )}
        </section>
      )}
    </main>
  );
}
